import { downloadFile } from "@/lib/utils/exporter";
import { apiGetRaw, apiPost } from "../_apiShim";

/**
 * Art. 15 / 20 data access for ONE guest — main-backend `DataExportResource`
 * (`/api/v1/data-export/events/{eventId}/guests/{guestId}`).
 *
 * Two steps: `request` mails a 6-digit code to the CALLER's own address
 * (10 min), the GET with that code in `X-Otp` returns the guest's portable
 * JSON (incl. the stored consent rows with wording, time and source). The
 * server checks the caller's role in the EVENT's company (OWNER / ADMIN /
 * EDITOR) before it mints or consumes a code; "no such event" and "not
 * yours" are the same 404 `eventNotFound`.
 *
 * Throws, like every client in this folder (`rethrowApi`: the backend code on
 * `.message`).
 */
const BASE_PATH = "/api/v1/data-export/events/";
const NO_STORE = { "Cache-Control": "no-store" } as const;

const guestPath = (eventId: string, guestId: string) =>
  `${BASE_PATH}${encodeURIComponent(eventId)}/guests/${encodeURIComponent(guestId)}`;

export async function requestGuestDataExportCode(eventId: string, guestId: string): Promise<void> {
  await apiPost<void>(`${guestPath(eventId, guestId)}/request`, undefined, NO_STORE);
}

/**
 * Saves the export as the server sent it — the BYTES are re-wrapped, not
 * parsed (a re-serialised body could round big numbers, and a foreign Blob is
 * stringified by jsdom on Node 22, see `downloadCompanyDpaPdf`). Nothing is
 * kept: no query cache, no storage.
 */
export async function downloadGuestDataExport(
  eventId: string,
  guestId: string,
  otp: string,
  fileName: string,
): Promise<{ fileName: string }> {
  const res = await apiGetRaw(guestPath(eventId, guestId), {
    ...NO_STORE,
    Accept: "application/json",
    "X-Otp": otp,
  });
  const bytes = await res.arrayBuffer();
  downloadFile(new Blob([bytes], { type: "application/json" }), fileName);
  return { fileName };
}
